'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface RegionData {
  id: string;
  name: string;
  country: string;
  population: number;
  area: string;
  coordinates: { lat: number; lng: number };
  status: string;
  lastUpdated: string;
}

interface RegionalDataMapProps {
  regions: RegionData[];
}

const RegionalDataMap = ({ regions }: RegionalDataMapProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<RegionData | null>(null);

  useState(() => {
    setIsHydrated(true);
  });

  const getStatusColor = (status: string) => {
    const s = status.toLowerCase().trim();
    if (s === 'critical') return 'status-critical';
    if (s === 'stable' || s === 'stable/recovering') return 'status-stable';
    if (s.includes('fragile')) return 'status-stable-fragile';
    if (s === 'improving') return 'status-improving';
    if (s.includes('recovering') || s.includes('historically') || s === 'declining') return 'status-recovering';
    return 'bg-muted text-muted-foreground';
  };

  const getStatusLabel = (status: string) => {
    const s = status.toLowerCase().trim();
    if (s === 'critical') return 'Critical';
    if (s === 'stable') return 'Stable';
    if (s === 'stable/recovering') return 'Stable / Recovering';
    if (s.includes('fragile')) return 'Stable (Fragile)';
    if (s === 'improving') return 'Improving';
    if (s.includes('recovering') || s.includes('historically')) return 'Recovering But Historically Declined';
    if (s === 'declining') return 'Declining';
    return status;
  };

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-md p-6">
        <div className="h-8 w-48 bg-muted rounded-md animate-pulse mb-4"></div>
        <div className="h-96 bg-muted rounded-md animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-md p-4 lg:p-6 shadow-organic-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-headline font-bold text-foreground">Regional Distribution</h3>
        <div className="flex items-center space-x-2 text-sm font-body text-muted-foreground">
          <Icon name="MapPinIcon" size={18} variant="outline" />
          <span>{regions.length} Protected Areas</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map Section */}
        <div className="lg:col-span-2">
          <div className="relative w-full h-96 bg-muted rounded-md overflow-hidden">
            <iframe
              width="100%"
              height="100%"
              loading="lazy"
              title="Rhino Conservation Regions Map"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=-24.9917,31.5917&z=5&output=embed"
              className="border-0"
            />
            <div className="absolute top-4 right-4 bg-card border border-border rounded-md p-3 shadow-organic">
              <div className="flex items-center space-x-2 text-xs font-body">
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 rounded-full bg-success"></div>
                  <span>Improving</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 rounded-full bg-success border border-dashed border-white/70"></div>
                  <span>Recovering</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 rounded-full bg-warning"></div>
                  <span>Stable</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 rounded-full bg-destructive"></div>
                  <span>Declining</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Region List */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {regions.map((region) => (
            <button
              key={region.id}
              onClick={() => setSelectedRegion(region)}
              className={`w-full text-left p-4 rounded-md border transition-organic duration-300 ${
                selectedRegion?.id === region.id
                  ? 'bg-primary/10 border-primary' :'bg-background border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="text-sm font-headline font-bold text-foreground">{region.name}</h4>
                  <p className="text-xs font-body text-muted-foreground">{region.country}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-body font-semibold ${getStatusColor(region.status)}`}>
                  {getStatusLabel(region.status)}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs font-body text-muted-foreground">
                <span>{region.population.toLocaleString()} rhinos</span>
                <span>{region.area}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Selected Region Details */}
      {selectedRegion && (
        <div className="mt-6 p-4 bg-muted rounded-md border border-border">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-lg font-headline font-bold text-foreground mb-1">{selectedRegion.name}</h4>
              <p className="text-sm font-body text-muted-foreground">{selectedRegion.country}</p>
            </div>
            <button
              onClick={() => setSelectedRegion(null)}
              className="p-2 text-muted-foreground hover:text-foreground transition-organic duration-300"
            >
              <Icon name="XMarkIcon" size={20} variant="outline" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-body text-muted-foreground mb-1">Population</p>
              <p className="text-xl font-headline font-bold text-foreground">{selectedRegion.population.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs font-body text-muted-foreground mb-1">Area</p>
              <p className="text-xl font-headline font-bold text-foreground">{selectedRegion.area}</p>
            </div>
            <div>
              <p className="text-xs font-body text-muted-foreground mb-1">Status</p>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-body font-semibold ${getStatusColor(selectedRegion.status)}`}>
                {getStatusLabel(selectedRegion.status)}
              </span>
            </div>
            <div>
              <p className="text-xs font-body text-muted-foreground mb-1">Last Updated</p>
              <p className="text-sm font-body text-foreground">{selectedRegion.lastUpdated}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegionalDataMap;